import React, { useEffect, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar.jsx';
import Footer from './components/Footer.jsx';
import Home from './pages/Home.jsx'; // Eagerly load the Home layout for rapid LCP

// Lazy load hubs, products, and long-form articles
const SolutionsHub = React.lazy(() => import('./pages/Services.jsx'));
const ResourceCenter = React.lazy(() => import('./pages/Blog.jsx'));
const UseCaseTemplate = React.lazy(() => import('./pages/use-cases/UseCaseTemplate.jsx'));
const LLMWikiLeadMagnet = React.lazy(() => import('./pages/resources/LLMWikiLeadMagnet.jsx'));
const Contact = React.lazy(() => import('./pages/Contact.jsx'));
const ProductsHub = React.lazy(() => import('./pages/products/ProductsHub.jsx'));
const ProductDetail = React.lazy(() => import('./pages/products/ProductDetail.jsx'));
const SuiteCRMHub = React.lazy(() => import('./pages/SuiteCRMHub.jsx'));
const UseCasesListing = React.lazy(() => import('./pages/UseCases.jsx'));
const NotFound = React.lazy(() => import('./pages/NotFound.jsx'));

// Service sub-pages
const SuiteCRMImplementation = React.lazy(() => import('./pages/services/SuiteCRMImplementation.jsx'));
const SuiteCRMCustomisation = React.lazy(() => import('./pages/services/SuiteCRMCustomisation.jsx'));
const CRMIntegration = React.lazy(() => import('./pages/services/CRMIntegration.jsx'));
const AIWorkflowAutomation = React.lazy(() => import('./pages/services/AIWorkflowAutomation.jsx'));
const SuiteCRMSupport = React.lazy(() => import('./pages/services/SuiteCRMSupport.jsx'));

// Individual blog post routes
const SuiteCRMAIAge = React.lazy(() => import('./pages/blogs/SuiteCRMAIAge.jsx'));
const CRMProcessNotSoftware = React.lazy(() => import('./pages/blogs/CRMProcessNotSoftware.jsx'));
const DonorRetentionSuiteCRM = React.lazy(() => import('./pages/blogs/DonorRetentionSuiteCRM.jsx'));
const ModernizingBusinessOperations = React.lazy(() => import('./pages/blogs/ModernizingBusinessOperations.jsx'));
const OpenClawSuiteCRMIntegration = React.lazy(() => import('./pages/blogs/OpenClawSuiteCRMIntegration.jsx'));
const SuiteCRM8DockerizedEnvironment = React.lazy(() => import('./pages/blogs/SuiteCRM8DockerizedEnvironment.jsx'));


// Lightweight pristine loader for Suspense Fallbacks
const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', background: '#fafbff' }}>
    <div style={{ width: '40px', height: '40px', border: '3px solid var(--border-light)', borderTopColor: 'var(--primary-color)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }}>
      <style>{`@keyframes spin { 100% { transform: rotate(360deg); } }`}</style>
    </div>
  </div>
);

/**
 * The root component orchestrates the assembly of all sections of the site. It
 * utilizes React Code Splitting to drastically optimize Core Web Vitals.
 */
const App = () => {

  // Add or remove scrolled class from navbar
  useEffect(() => {
    const handleScroll = () => {
      const navbar = document.getElementById('navbar');
      if (window.scrollY > 50) {
        navbar?.classList.add('scrolled');
      } else {
        navbar?.classList.remove('scrolled');
      }
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Apply a simple parallax effect to elements with the data-parallax attribute
  useEffect(() => {
    const parallaxEls = document.querySelectorAll('[data-parallax]');
    const onScroll = () => {
      const scrollY = window.scrollY;
      parallaxEls.forEach((el) => {
        const speed = parseFloat(el.getAttribute('data-parallax')) || 0.1;
        el.style.transform = `translateY(${scrollY * speed}px)`;
      });
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Natural page opening from top whenever the route changes
  const location = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location]);

  // IntersectionObserver to reveal elements on initial load and whenever the route changes.
  useEffect(() => {
    const revealElements = document.querySelectorAll('.reveal');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    revealElements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [location]);

  const handleLinkClick = () => {
    // Optional callback for handling link clicks globally
  };

  return (
    <>
      <Navbar onLinkClick={handleLinkClick} />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/services" element={<SolutionsHub />} />
          <Route path="/solutions" element={<Navigate to="/services" replace />} />
          <Route path="/services/suitecrm-implementation" element={<SuiteCRMImplementation />} />
          <Route path="/services/suitecrm-customisation" element={<SuiteCRMCustomisation />} />
          <Route path="/services/crm-integration" element={<CRMIntegration />} />
          <Route path="/services/ai-workflow-automation" element={<AIWorkflowAutomation />} />
          <Route path="/services/suitecrm-support" element={<SuiteCRMSupport />} />
          <Route path="/products" element={<ProductsHub />} />
          <Route path="/products/:productId" element={<ProductDetail />} />
          <Route path="/suitecrm" element={<SuiteCRMHub />} />
          <Route path="/developers" element={<Navigate to="/suitecrm" replace />} />
          <Route path="/use-cases" element={<UseCasesListing />} />
          <Route path="/resources" element={<ResourceCenter />} />
          <Route path="/use-cases/:slug" element={<Suspense fallback={<div className="loading-spinner">Loading...</div>}><UseCaseTemplate /></Suspense>} />
          <Route path="/resources/llm-wiki-crm" element={<Suspense fallback={<div className="loading-spinner">Loading...</div>}><LLMWikiLeadMagnet /></Suspense>} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/contact-us" element={<Navigate to="/contact" replace />} />
          {/* Individual blog post routes */}
          <Route path="/blog/suitecrm-ai-age" element={<SuiteCRMAIAge />} />
          <Route path="/blog/crm-process-not-software" element={<CRMProcessNotSoftware />} />
          <Route path="/blog/donor-retention-suitecrm" element={<DonorRetentionSuiteCRM />} />
          <Route path="/blog/modernizing-business-operations" element={<ModernizingBusinessOperations />} />
          <Route path="/blog/openclaw-suitecrm-integration" element={<OpenClawSuiteCRMIntegration />} />
          <Route path="/blog/suitecrm-8-dockerized-environment" element={<SuiteCRM8DockerizedEnvironment />} />
          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      <Footer />
    </>
  );
};

export default App;
